'use client';

import { QueryClient, QueryClientProvider, QueryFunction, QueryCache } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { ThemeProvider } from 'next-themes';
import { apiFetch } from '@/core/utils/api-client';
import { TimerProvider } from '@/core/contexts/timer-context';

const defaultQueryFn: QueryFunction = async ({ queryKey }) => {
  // Build URL from query key parts - join path segments with /
  const urlParts = queryKey.filter((part): part is string => typeof part === 'string');
  const url = urlParts.join('/').replace(/\/+/g, '/'); // Prevent double slashes

  const res = await apiFetch(url);

  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }

  return res.json();
};

export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            queryFn: defaultQueryFn,
            staleTime: 5 * 60 * 1000, // 5 minutes - most data changes infrequently
            gcTime: 10 * 60 * 1000, // 10 minutes - keep unused data in cache
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
              // Don't retry on auth errors
              if (error.message.startsWith('401') || error.message.startsWith('403')) {
                return false;
              }
              return failureCount < 3;
            },
          },
        },
        queryCache: new QueryCache({
          onError: (error) => {
            // Redirect to sign-in on 401
            if (error.message.startsWith('401')) {
              window.location.href = '/auth/sign-in';
            }
          },
        }),
      })
  );

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js');
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <TimerProvider>
          {children}
        </TimerProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
